import React from "react";

// Format a date string like "25 February 2026"
function formatElegantDate(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function LetterRow({ conversation, individualId, onClick, isPinned, onTogglePin }) {
  // Find the other person in the conversation (not you)
  const otherParticipant = conversation.participants?.find(
    (p) => p.individual_id !== individualId
  );
  // If no "other" found (you sent to yourself), use your own name
  const selfParticipant = conversation.participants?.find(
    (p) => p.individual_id === individualId
  );
  const displayName = otherParticipant?.individual_name || selfParticipant?.individual_name || "Unknown";
  const initial = displayName.charAt(0).toUpperCase();

  const lastDate = conversation.last_message?.created_at || conversation.created_at;
  const isUnread = conversation.unread_count > 0;

  // Truncate subject if too long
  const subject =
    conversation.subject?.length > 50
      ? conversation.subject.slice(0, 50) + "..."
      : conversation.subject;

  return (
    // The row holds two controls side by side: the letter (opens it) and the
    // favourite star (pins it). Neither sits inside the other. The letter's
    // button stretches over the whole row (its ::after), so a click anywhere
    // opens it, as before; the star sits above that layer.
    <div className="inbox-row relative">
      <button
        type="button"
        onClick={onClick}
        className="flex flex-row items-center gap-x-[16px] flex-1 min-w-0 text-left
                   focus:outline-none after:content-[''] after:absolute after:inset-0 after:rounded-[8px]
                   focus-visible:after:ring-2 focus-visible:after:ring-dupontGold"
      >
        {/* Wax seal with sender's initial (the name follows, so it isn't read twice) */}
        <span className="wax-seal" aria-hidden="true">{initial}</span>

        {/* Name, subject, and date */}
        <span className="flex flex-col flex-1 min-w-0 gap-y-[2px]">
          <span className="flex flex-row items-center justify-between gap-x-[12px]">
            <span
              className={`text-[16px] font-display truncate ${
                isUnread ? "font-semibold text-dupontBlue dark:text-darkText" : "font-normal text-dupontBlue/80 dark:text-darkTextSecondary"
              }`}
            >
              {displayName}
            </span>
            <span className="text-[13px] font-body text-dupontGray dark:text-darkTextSecondary italic whitespace-nowrap">
              {formatElegantDate(lastDate)}
            </span>
          </span>
          <span
            className={`text-[14px] font-body truncate ${
              isUnread ? "text-dupontBlue dark:text-darkText" : "text-dupontGray dark:text-darkTextSecondary"
            }`}
          >
            {subject}
          </span>
        </span>
      </button>

      {/* Pin / Favorite star */}
      <button
        type="button"
        onClick={() => onTogglePin && onTogglePin(conversation.id)}
        className="relative z-10 ml-3 flex-shrink-0 p-1 transition-colors duration-150 hover:scale-110"
        title={isPinned ? "Remove from Favorites" : "Add to Favorites"}
      >
        {isPinned ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-dupontGold">
            <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-dupontGray dark:text-darkTextSecondary hover:text-dupontGold">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
          </svg>
        )}
      </button>

      {/* Unread dot */}
      {isUnread && <div className="unread-dot" />}
    </div>
  );
}

export default LetterRow;
